import React from 'react';
import {
  IonModal,
  IonButton,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonIcon,
  IonSpinner,
} from '@ionic/react';
import { closeOutline, addOutline, folderOutline } from 'ionicons/icons';
import './ProjectPickerForDeployModal.css';

/**
 * Open when a user clicks Deploy on the public marketplace and lands on
 * /projects?deployTemplate=<slug> with at least one existing project. The
 * 0-project case skips this modal — ProjectsPage routes straight to the
 * combined Create-and-Deploy form because there is nothing to pick.
 *
 * Props:
 *   isOpen
 *   onDismiss               — close without picking
 *   projects                — array of { projectId, name }
 *   templateName            — display name of the template being deployed
 *   templateIcon            — optional icon URL
 *   onSelectProject(pid)    — async; parent runs Marketplace.deployWorkflow
 *                             and navigates on success
 *   onCreateNewProject()    — switch to the Create-and-Deploy modal
 *   deploying               — disables the row while the parent's deploy
 *                             POST is in flight
 *   deployingProjectId      — which row is currently being deployed (for
 *                             the per-row spinner)
 */
const ProjectPickerForDeployModal = ({
  isOpen,
  onDismiss,
  projects = [],
  templateName,
  templateIcon,
  onSelectProject,
  onCreateNewProject,
  deploying = false,
  deployingProjectId = null,
}) => {
  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      className="project-picker-modal"
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>Deploy template</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss} disabled={deploying}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div className="ppd-body">
          <div className="ppd-header">
            {templateIcon ? (
              // eslint-disable-next-line jsx-a11y/img-redundant-alt
              <img
                src={templateIcon}
                alt=""
                className="ppd-template-icon"
              />
            ) : (
              <span className="ppd-template-icon ppd-template-icon--fallback">
                {(templateName || '?').slice(0, 1).toUpperCase()}
              </span>
            )}
            <div className="ppd-header-text">
              <div className="ppd-template-name">{templateName || 'this template'}</div>
              <div className="ppd-prompt">Pick a project to deploy into.</div>
            </div>
          </div>

          <ul className="ppd-project-list">
            {projects.map((p) => {
              const rowBusy = deploying && deployingProjectId === p.projectId;
              return (
                <li key={p.projectId} className="ppd-project-row-li">
                  <button
                    type="button"
                    className="ppd-project-row"
                    onClick={() => onSelectProject(p.projectId)}
                    disabled={deploying}
                  >
                    <IonIcon icon={folderOutline} className="ppd-project-icon" />
                    <span className="ppd-project-name">{p.name}</span>
                    {rowBusy ? (
                      <IonSpinner name="crescent" className="ppd-row-spinner" />
                    ) : (
                      <span className="ppd-project-action">Deploy</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          <button
            type="button"
            className="ppd-create-new"
            onClick={onCreateNewProject}
            disabled={deploying}
          >
            <IonIcon icon={addOutline} />
            <span>Create a new project</span>
          </button>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default ProjectPickerForDeployModal;
